import { useRef, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, RotateCcw, Loader2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Captura de foto AO VIVO pela câmera do navegador + upload -> retorna path via onCaptured
export default function CameraCapture({ onCaptured }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    setPreview(null);
    try {
      // No iOS/PWA o getUserMedia precisa ser chamado dentro do gesto do usuário (botão)
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setReady(true);
      setErr("");
    } catch (e) {
      setReady(false);
      const n = (e && (e.name || e.message)) || "";
      setErr(/NotAllowed|Permission|Denied/i.test(n)
        ? "Permissão de câmera negada. No iPhone: Ajustes › Safari (ou o app NEXO LOG) › Câmera › Permitir; no Android habilite a câmera nas permissões do site."
        : "Não foi possível acessar a câmera. Toque para tentar novamente.");
    }
  }, []);

  // iOS/PWA: NÃO inicia automaticamente; o usuário toca em "Abrir câmera".
  useEffect(() => stop, [stop]);

  const capture = async () => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return;
    setPreview(URL.createObjectURL(blob));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `avaria_${Date.now()}.jpg`);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCaptured(data.path);
      toast.success("Foto capturada e enviada");
      stop();
      setReady(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar foto");
      setPreview(null);
    } finally { setUploading(false); }
  };

  const retake = () => { setPreview(null); onCaptured(null); start(); };

  return (
    <div className="rounded-lg border border-rose-500/50 bg-black/50 p-3 space-y-3" data-testid="camera-capture">
      <div className="text-rose-300 text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-2">
        <Camera className="w-4 h-4" /> Foto da Avaria (obrigatória, ao vivo)
      </div>
      <div className="relative rounded-md overflow-hidden bg-black aspect-video flex items-center justify-center">
        {!preview ? (
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="camera-video" />
        ) : (
          <img src={preview} alt="Avaria" className="w-full h-full object-cover" data-testid="camera-preview" />
        )}
        {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-rose-300" /></div>}
      </div>
      <canvas ref={canvasRef} className="hidden" />
      {!preview ? (
        ready ? (
          <Button type="button" className="w-full h-12" onClick={capture} data-testid="btn-capturar-foto">
            <Camera className="w-4 h-4" /> Capturar Foto
          </Button>
        ) : (
          <div className="space-y-2">
            {err && <div className="flex items-start gap-2 rounded-md border border-red-600/50 bg-red-950/50 p-2.5 text-xs text-red-200" data-testid="cam-error"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{err}</span></div>}
            <Button type="button" className="w-full h-12" onClick={start} data-testid="btn-abrir-camera">
              <Camera className="w-4 h-4" /> Abrir câmera
            </Button>
          </div>
        )
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1 text-emerald-300 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Foto anexada</div>
          <Button type="button" variant="outline" size="sm" onClick={retake} data-testid="btn-refazer-foto"><RotateCcw className="w-4 h-4" /> Refazer</Button>
        </div>
      )}
    </div>
  );
}
